import type { MessageSchema } from './sl'

/* English — the same keys as `sl.ts` (`satisfies MessageSchema`). Plurals: one | other. */
export default {
    general: {
        add:       'Add',
        edit:      'Edit',
        delete:    'Delete',
        cancel:    'Cancel',
        close:     'Close',
        confirm:   'Confirm',
        save:      'Save',
        saving:    'Saving …',
        back:      'Back',
        refresh:   'Refresh',
        searching: 'Searching …',
        sign_out:  'Sign out',
        total:     'Total: {n}',
        yes:       'yes',
        no:        'no',
    },
    brand: {
        product_name: 'Portal',
        tagline:      'Galerija Gosposka',
    },
    nav: {
        dashboard:       'Overview',
        group_dobavnice: 'Delivery notes',
        uploads:         'Upload',
        notes:           'List',
        exports:         'Export',
        suppliers:       'Suppliers',
        catalogs:        'Catalogs',
        group_inventura: 'Stock count',
        inventories:     'Stock counts',
        group_admin:     'Administration',
        users:           'Users',
        roles:           'Roles',
        settings:        'Settings',
        profile:         'Profile',
        version_web:     'Console {version}',
        version_api:     'API {version}',
        articles: 'Bar articles',
        group_prenos: 'Transfers',
        transfers: 'Transfer notes',
        transfer_new: 'New transfer note',
        group_urniki: 'Schedules',
        schedule:     'Schedule',
        my_schedule:  'My shifts',
        employees:    'Employees',
        group_eracuni: 'E-invoices',
        eracuni_log:   'Log',
    },
    layout: {
        open_menu:  'Open menu',
        close_menu: 'Close menu',
    },
    color_mode: {
        switch_to_light: 'Switch to light mode',
        switch_to_dark:  'Switch to dark mode',
    },
    locale: {
        label: 'Language',
        sl:    'Slovenščina',
        en:    'English',
    },
    date: {
        clear: 'Clear date',
        pick:  'Pick a date',
    },
    auth: {
        username:      'Username',
        password:      'Password',
        password_show: 'Show password',
        password_hide: 'Hide password',
        sign_in:       'Sign in',
        failed:        'Wrong username or password.',
    },
    errors: {
        generic_error:     'An error occurred.',
        internal:          'Server error. Please try again in a moment.',
        invalid_data:      'The data is not valid.',
        not_found:         'Not found.',
        unauthorized:      'Your session has expired. Please sign in again.',
        failed_to_insert:  'The record could not be saved.',
        forbidden:         'You do not have permission for this.',
        too_many_attempts: 'Too many failed sign-ins. Try again in a few minutes.',
        conflict:          'The record changed meanwhile, or this step is not possible in its current state.',
        duplicate:         'Such a record already exists.',
        ai_not_configured: 'AI is not configured (no key, or switched off). Set it up in Settings → AI.',
        ai_failed:         'The AI call failed.',
        ai_failed_reply:   'The AI call failed: {reply}',
        last_admin:        'The last active administrator cannot lose the users permission, be deactivated or removed.',
        cannot_confirm:    'The delivery note cannot be confirmed while error checks remain.',
        import_unreadable: 'The file cannot be read (wrong format or a required column is missing).',
        wrong_password:    'The current password is wrong.',
        inventory_open:    'A stock count of this restaurant is already open (counting or review).',
        mail_failed:       'The e-mail could not be sent.',
        mail_failed_reply: 'The e-mail could not be sent: {reply}',
        on_load:           'The data could not be loaded.',
        on_add:            'Error while adding.',
        on_edit:           'Error while saving.',
        on_delete:         'Error while deleting.',
    },
    success: {
        add:    'Added.',
        edit:   'Saved.',
        delete: 'Deleted.',
    },
    validation: {
        required:   'This field is required.',
        email:      'Invalid e-mail address.',
        too_long:   'Too long.',
        min_length: 'At least {n} characters.',
    },
    reason: {
        label: 'Reason',
    },
    module: {
        admin:     'Administration',
        dobavnice: 'Delivery notes',
        inventura: 'Stock count',
        prenos: 'Transfers',
        urniki: 'Schedules',
        eracuni: 'E-invoices',
    },
    permission: {
        admin_users:        'Users and roles',
        admin_settings:     'AI and mail settings',
        dobavnice_view:     'View',
        dobavnice_upload:   'Upload scans',
        dobavnice_review:   'Review and confirm',
        dobavnice_export:   'Export to accounting',
        dobavnice_catalogs: 'Edit catalogs',
        dobavnice_settings: 'Export settings',
        inventura_view:     'View',
        inventura_count:    'Count',
        inventura_manage:   'Manage stock counts',
        inventura_export:   'Download the Excel report',
        inventura_articles: 'Bar articles',
        prenos_view: 'View',
        prenos_create: 'Issue transfer notes',
        prenos_receive: 'Confirm receipt',
        prenos_manage: 'Manage transfers',
        urniki_view: 'View',
        urniki_edit: 'Edit schedules',
        eracuni_view:   'View the log',
        eracuni_manage: 'Manage',
    },
    permission_help: {
        admin_users:        'Creates and edits users and roles.',
        admin_settings:     'Sets the Anthropic key, the model, parallel reading and the portal\'s outgoing mail.',
        dobavnice_view:     'Reads delivery notes, uploads, exports and catalogs.',
        dobavnice_upload:   'Uploads scans and retries a failed reading.',
        dobavnice_review:   'Corrects, confirms, rejects, reopens and re-reads delivery notes.',
        dobavnice_export:   'Creates, downloads and cancels XML exports.',
        dobavnice_catalogs: 'Edits and imports materials, suppliers and warehouses; deletes mappings.',
        dobavnice_settings: 'Edits the XML export format (VAT groups, decimals, encoding).',
        inventura_view:     'Reads stock counts; book quantities only together with managing.',
        inventura_count:    'Counts: adds and removes their own entries, adds an item not on the list, hands a count over to review.',
        inventura_manage:   'Opens a bar count, sees opening, added, sold and result, imports sales, corrects, closes, reopens, cancels; the settings.',
        inventura_export:   'Downloads the stock count report as Excel.',
        inventura_articles: 'Edits the bar articles: each place\'s list and order, merging, importing a count sheet.',
        prenos_view: 'Reads the transfer notes of the person\'s places.',
        prenos_create: 'Makes, edits (draft), issues and cancels own transfer notes.',
        prenos_receive: 'Confirms the receipt of a transfer at the person\'s place.',
        prenos_manage: 'Cancels any transfer note and reads all.',
        urniki_view: 'Sees published schedules and their own shifts.',
        urniki_edit: 'Edits shifts, days, employees and the schedule settings; sees drafts and publishes them.',
        eracuni_view:   'Reads the log of e-invoices sent and the folder\'s status.',
        eracuni_manage: 'Edits the e-invoice settings and sends a file of the log again.',
    },
    dashboard: {
        review:          'In review',
        confirmed:       'Confirmed',
        exported:        'Exported',
        rejected:        'Rejected',
        uploads_pending: 'Being read',
        uploads_failed:  'Failed readings',
        recent_uploads:  'Recent uploads',
        no_uploads:      'No uploads yet.',
        welcome:         'Welcome, {name}',
        no_modules:      'You have no access to any module yet. Ask an administrator for access.',
        inventura_counting: 'Being counted',
        inventura_review:   'In review',
        inventura_closed:   'Closed (30 days)',
        prenos_to_receive: 'Waiting for my receipt',
        prenos_my_drafts: 'My drafts',
        prenos_in_transit: 'On their way',
        today_title:        'Today',
        my_shift:           'My shift today',
        receive_goods:      'Take over the goods',
        you_are_first:      'You are first in shift',
        receive_action:     'Receive',
        open_transfer:      'Open',
        bar_count_today:    'Bar count today',
        inventory_open:     'Inventory {number} is open.',
        inventory_not_open: 'The inventory is not open yet.',
        count_action:       'Count',
        open_inventory:     'Open the inventory',
        urniki_intro:       'Here are your shifts and the week\'s schedule.',
    },
    upload_status: {
        queued:  'Queued',
        reading: 'Reading',
        done:    'Read',
        failed:  'Failed',
    },
    upload: {
        title:             'Upload delivery notes',
        subtitle:          'PDF, JPG or PNG scans; one file may hold several delivery notes.',
        drop_hint:         'Drop files anywhere on the page or click here',
        drop_here:         'Drop the files to upload',
        drop_limits:       'PDF, JPG, PNG · up to {files} files · at most {mb} MB each',
        sending:           'Uploading {done} of {total} …',
        sent:              '{n} file uploaded. | {n} files uploaded.',
        refused_title:     '{n} file refused | {n} files refused',
        refused_type:      'not a PDF, JPG or PNG',
        refused_size:      'larger than {mb} MB',
        refused_empty:     'empty file',
        refused_duplicate: 'this file is already uploaded',
        refused_network:   'the connection dropped, try again',
        refused_other:     'refused',
        force:             'Upload anyway',
        list_title:        'Uploads',
        status_filter:     'Status',
        status_all:        'All statuses',
        empty:             'No uploads.',
        file:              'File',
        uploaded:          'Uploaded',
        status:            'Status',
        notes:             'Delivery notes',
        note_n:            'Note {n}',
        notes_count:       '{n} delivery note | {n} delivery notes',
        pages:             '{n} page | {n} pages',
        retry:             'Retry reading',
        retry_started:     'Reading started again.',
        retry_failed:      'The reading could not be retried.',
        share_badge:       'Scanner',
        share_badge_title: 'From the scanner folder: {name}',
        share_status:      'last look {time}, today {files}',
        share_never:       'not looked at yet',
        share_no_unit:     'No unit',
        share_files:       '{n} file | {n} files',
        share_refused:     'refused today: {n}',
        share_error:       'Last error: {error}',
    },
    note_status: {
        review:    'In review',
        confirmed: 'Confirmed',
        exported:  'Exported',
        rejected:  'Rejected',
    },
    note_status_tab: {
        review:    'In review',
        confirmed: 'Confirmed',
        exported:  'Exported',
        rejected:  'Rejected',
    },
    document_type: {
        dobavnica:       'Delivery note',
        racun:           'Invoice',
        racun_dobavnica: 'Invoice and delivery note',
        drugo:           'Other',
    },
    line_match: {
        mapped:    'Learned',
        confirmed: 'Confirmed',
        suggested: 'Suggestion',
        none:      'No material',
    },
    check: {
        supplier_missing:       'The supplier is missing.',
        supplier_unknown:       'Tax ID {tax_id} is not in the supplier catalog.',
        tax_id_invalid:         'Tax ID {tax_id} is not valid (check digit).',
        number_missing:         'The document number is missing.',
        date_missing:           'The document date is missing.',
        date_future:            'The document date ({date}) is in the future.',
        warehouse_missing:      'No warehouse is chosen.',
        line_material_missing:  'Line {line}: no material chosen, or the suggestion is not accepted.',
        line_vat_group_missing: 'Line {line}: the VAT group is missing.',
        line_quantity_missing:  'Line {line}: the quantity is missing.',
        sum_mismatch:           'The sum of the lines ({lines}) does not match the document\'s net amount ({header}).',
        sum_mismatch_no_header: 'The document\'s net amount is missing (the lines sum to {lines}).',
        line_net_mismatch:      'Line {line}: quantity × price − discount gives {expected}, the printed net is {net}.',
        duplicate:              'A delivery note with the same tax ID and number already exists.',
        no_lines:               'The delivery note has no lines.',
        buyer_as_supplier:      'Galerija Gosposka (the buyer, {tax_id}) is entered as the supplier.',
        unit_factor_missing:    'Line {line}: the supplier\'s unit ({unit}) is not the material\'s ({material_unit}) — enter how many {material_unit} one {unit} is.',
    },
    confidence: {
        high:   'very likely',
        medium: 'likely',
        low:    'a guess',
    },
    factor_source: {
        auto:    'automatic',
        mapping: 'learned',
        set:     'entered',
    },
    note: {
        list_title:         'Delivery notes',
        search:             'Search by number or supplier',
        all_warehouses:     'All warehouses',
        all_suppliers:      'All suppliers',
        from:               'From date',
        to:                 'To date',
        clear_filters:      'Clear filters',
        empty:              'No delivery notes.',
        date:               'Date',
        number:             'Number',
        no_number:          '(no number)',
        supplier:           'Supplier',
        warehouse:          'Warehouse',
        warehouse_pick:     'Choose a warehouse',
        net_total:          'Net',
        vat_total:          'VAT',
        gross_total:        'Gross',
        checks:             'Checks',
        checks_ok:          'All checks pass.',
        checks_stale:       'Refreshed on save.',
        errors_count:       '{n} error | {n} errors',
        warnings_count:     '{n} warning | {n} warnings',
        status:             'Status',
        read_by:            'Read by {model}, {when}',
        edited_by:          'last edited by {name}, {when}',
        previous:           'Previous in review',
        next:               'Next in review',
        position:           '{n} / {total}',
        in_review:          'In review: {total}',
        header_title:       'Document header',
        supplier_tax_id:    'Supplier tax ID / VAT ID',
        supplier_name:      'Supplier name',
        supplier_matched:   'In catalog',
        supplier_unmatched: 'Not in catalog',
        delivery_address:   'Delivery address: {address}',
        buyer:              'Buyer on the document: {buyer}',
        warehouse_from_folder: 'The unit of the scanner folder the scan came from.',
        document_type:      'Document type',
        service_date:       'Date of service',
        ai_notes:           'Reading notes',
        lines_title:        'Lines ({n})',
        no_lines:           'No lines.',
        sum_lines:          'Lines {lines} · header {header}',
        scan_title:         'Original scan',
        scan_pages:         'Pages {pages}',
        scan_open:          'Open in a new tab',
        scan_zoom_in:       'Actual size',
        scan_zoom_out:      'Fit to width',
        confirm:            'Confirm',
        confirm_blocked:    'Fix {n} error first. | Fix {n} errors first.',
        confirmed:          'The delivery note is confirmed.',
        confirm_failed:     'The delivery note could not be confirmed.',
        confirmed_title:    'Confirmed by {name}, {when}',
        saved:              'Saved.',
        conflict:           'Somebody else changed this delivery note meanwhile.',
        conflict_help:      'The current version is loaded; your changes were not saved.',
        gone:               'This delivery note no longer exists.',
        gone_help:          'It was read again meanwhile (a new one was made) or removed. Opening the notes in review.',
        reject:             'Reject',
        reject_title:       'Reject the delivery note',
        reject_help:        'A rejected delivery note is never exported. The reason is kept.',
        rejected:           'The delivery note is rejected.',
        reject_failed:      'The delivery note could not be rejected.',
        rejected_title:     'Rejected',
        reopen:             'Reopen',
        reopen_question:    'Return this confirmed delivery note to review? It will no longer be ready for export.',
        reopened:           'The delivery note is back in review.',
        reopen_failed:      'The delivery note could not be reopened.',
        reread:             'Read again',
        reread_question:    'Read the file again? The notes from this file in review or rejected will be replaced; your corrections will be lost.',
        reread_started:     'The file is being read again.',
        reread_failed:      'The reading could not be started.',
        exported_title:     'Exported',
        exported_help:      'An exported delivery note cannot be edited until its export is cancelled.',
        leave_title:        'Unsaved changes',
        leave_question:     'The changes to this delivery note are not saved. Discard them?',
        leave:              'Discard',
        suggest:              'Suggest with AI',
        suggest_running:      'Claude is suggesting …',
        suggest_help:         'Claude suggests a material (and a unit conversion) for the lines the server could not place. A suggestion counts only once you accept it.',
        suggest_done:         'Claude suggested {when}. Ask again for lines without a material.',
        suggest_ready:        'Claude\'s suggestions are ready — review and accept them.',
        suggest_failed:       'AI suggestions failed',
        suggest_arrived:      'Claude\'s suggestions have arrived.',
        suggest_arrived_help: 'The note has unsaved changes: saving will report a conflict and reload it with the suggestions.',
    },
    line: {
        supplier_code:        'Code',
        description:          'Description',
        quantity:             'Quantity',
        unit_price:           'Price',
        discount:             'Disc. %',
        net_amount:           'Net',
        vat_rate:             'VAT %',
        vat_group:            'VAT group',
        material:             'Material',
        lot:                  'Lot {lot}',
        accept_suggestion:    'Accept the suggestion',
        add:                  'Add a line',
        remove_of:            'Remove line {n}',
        description_of:       'Description, line {n}',
        supplier_code_of:     'Supplier code, line {n}',
        quantity_of:          'Quantity, line {n}',
        unit_price_of:        'Unit price, line {n}',
        discount_of:          'Discount percentage, line {n}',
        net_amount_of:        'Net amount, line {n}',
        vat_rate_of:          'VAT rate, line {n}',
        vat_group_of:         'VAT group, line {n}',
        material_of:          'Material, line {n}',
        factor_missing:       'how much?',
        factor_of:            'How many {material_unit} one {unit} is, line {n}',
        converted_of:         'Quantity in {unit}, line {n}',
        claude_factor:        'Claude: 1 {unit} = {factor} {material_unit}',
        claude_factor_help:   'Claude\'s proposed conversion — use it',
    },
    export: {
        pending_title:    'Ready for export',
        pending_subtitle: 'Confirmed delivery notes by unit — one XML file per unit.',
        nothing_pending:  'No confirmed delivery notes are waiting for export.',
        selected:         '{n} of {total} selected',
        select_all:       'Select all',
        select_note:      'Select delivery note {number}',
        create:           'Export',
        created_toast:    'Export {number} is ready (delivery notes: {n}); the download has started.',
        create_failed:    'The export could not be created.',
        history_title:    'Export history',
        history_empty:    'No exports yet.',
        number:           'Export no.',
        note_count:       'Notes',
        created:          'Created',
        state:            'Status',
        active:           'Valid',
        cancelled:        'Cancelled',
        download:         'Download XML',
        cancel:           'Cancel export',
        cancel_title:     'Cancel export {number}',
        cancel_help:      'The delivery notes of this export ({n}) return to confirmed. A file already in accounting is not withdrawn by itself.',
        cancelled_toast:  'Export {number} is cancelled.',
        cancel_failed:    'The export could not be cancelled.',
    },
    catalog: {
        title:              'Catalogs',
        subtitle:           'Materials, warehouses and learned mappings.',
        tab_materials:      'Materials',
        tab_warehouses:     'Warehouses',
        tab_mappings:       'Mappings',
        active:             'Active',
        only_active:        'Active',
        all:                'All',
        deactivate:         'Remove',
        deleted:            'Deleted.',
        deactivated_in_use: 'Deactivated (in use).',
        download_csv:       'Download CSV',
    },
    material: {
        search:              'Search by code or name',
        pick:                'Choose a material',
        suggestions:         'Suggestions',
        catalog:             'Catalog',
        claude:              'Claude',
        ai_suggestion:       'Claude\'s suggestion',
        none_found:          'No matches.',
        add:                 'Add material',
        edit:                'Edit material',
        code:                'Code',
        name:                'Name',
        unit:                'Unit',
        vat_group:           'VAT group',
        active_help:         'An inactive material is not offered on delivery notes.',
        empty:               'No materials.',
        deactivate_question: 'Remove material {code} {name}? If a mapping or a delivery note uses it, it is only deactivated.',
        import_title:        'Import materials',
        import_columns:      'Columns: code (šifra), name (naziv), unit (em), VAT group (ddv)',
    },
    supplier: {
        title:               'Suppliers',
        subtitle:            'The supplier catalog the server recognises each delivery note\'s supplier by.',
        search:              'Search by tax ID or name',
        pick:                'Choose a supplier',
        none_found:          'No matches.',
        add:                 'Add supplier',
        edit:                'Edit supplier',
        tax_id:              'Tax ID / VAT ID',
        tax_id_help:         'With the country prefix when it has one: SI42034086, IT00547460311. May stay empty.',
        tax_id_invalid:      'Invalid format (e.g. SI12345678).',
        no_tax_id:           'no tax ID',
        name:                'Name',
        aliases:             'Aliases',
        aliases_help:        'Other names the supplier appears under on documents (separate with Enter or a comma).',
        address:             'Address',
        country:             'Country',
        country_help:        'Two-letter code, e.g. SI, IT, AT.',
        country_invalid:     'Two letters, e.g. SI.',
        active_help:         'The server does not offer an inactive supplier when recognising.',
        empty:               'No suppliers.',
        deactivate_question: 'Remove supplier {name}? If a mapping or a delivery note uses it, it is only deactivated.',
        import_title:        'Import suppliers',
        import_columns:      'Columns: tax ID (davcna, ds, ddv_id), name, address, country',
        notes:               'Supplier\'s delivery notes',
        mapping_count:       'Mappings',
        mappings_title:      'Learned mappings',
        mappings_subtitle:   'Supplier codes that confirming has linked to our materials.',
    },
    warehouse: {
        search:              'Search warehouses',
        add:                 'Add warehouse',
        edit:                'Edit warehouse',
        code:                'Code',
        code_help:           'The warehouse code in accounting; the export file is named after it.',
        name:                'Name',
        keywords:            'Keywords',
        keywords_help:       'Words in the delivery address the server recognises the warehouse by (case and diacritics do not matter).',
        fallback:            'Fallback warehouse',
        fallback_help:       'Applies only when the document names no other warehouse (e.g. Rožmarin: "La Pizzeria Rožmarin" goes to La Pizzeria).',
        fallback_badge:      'fallback',
        empty:               'No warehouses.',
        deactivate_question: 'Remove warehouse {name}?',
        flags: 'Used for',
        bar: 'Bar',
        bar_help: 'The place has a bar: it is counted weekly and has its own article list.',
        bar_badge: 'bar',
        transfers: 'Transfers',
        transfers_help: 'The place can be the source or the target of a transfer note.',
        transfers_badge: 'transfers',
        main: 'Main warehouse',
        main_help: 'The default source of a new transfer note; only one warehouse can be the main one.',
        main_badge: 'main',
        color:      'Colour in the schedule',
        color_help: 'Shifts at this place are drawn in this colour on the schedule.',
        unit:                    'Unit',
        unit_help:               'One of the three units (La Pizzeria, Rožmarin, Poper): delivery notes and exports go by unit, every other place belongs to one of them.',
        unit_badge:              'unit',
        unit_of:                 'Unit',
        belongs_to:              'Belongs to unit',
        belongs_to_help:         'A user limited to the unit also sees its places (e.g. transfers to Vinoteka).',
        belongs_to_none:         'No unit',
        deactivate_unit_warning: 'The places {places} belong to the unit — they stay its places.',
        unit_inactive_warning:   'The places {places} belong to the unit. Switched off, they stay its places; it can no longer be chosen for delivery notes.',
        unit_members_warning:    'The places {places} belong to the unit. Move them to another unit first, or the server refuses the change.',
    },
    mapping: {
        search:          'Search by code or material',
        empty:           'No learned mappings.',
        supplier:        'Supplier',
        key:             'Supplier code / description',
        by_description:  'By the line\'s description (the supplier prints no code)',
        material:        'Our material',
        uses:            'Uses',
        last_used:       'Last used',
        delete_question: 'Delete the mapping {key} → {material}? The next confirmation may teach it again.',
        factor:          'Conversion',
        factor_value:    '1 {unit} = {factor} {material_unit}',
        factor_auto:     'by units',
    },
    import: {
        button:             'Import',
        description:        'A CSV file (; or , separated) or an XLSX with a header row.',
        file:               'File',
        file_help:          'CSV in UTF-8 or Windows-1250, or XLSX.',
        file_required:      'Choose a file.',
        drop:               'Drop the file here or click',
        mode:               'Mode',
        mode_upsert:        'Add and update',
        mode_upsert_help:   'New records are added, existing ones updated; the rest stay.',
        mode_replace:       'Replace',
        mode_replace_help:  'As above, and records missing from the file are deactivated.',
        start:              'Import',
        failed:             'The import failed.',
        created:            'Added',
        updated:            'Updated',
        deactivated:        'Deactivated',
        skipped:            '{n} row skipped | {n} rows skipped',
        row:                'row {row}',
    },
    inventory_status: {
        counting:  'Counting',
        review:    'Review',
        closed:    'Closed',
        cancelled: 'Cancelled',
    },
    inventory_status_tab: {
        counting:  'Counting',
        review:    'Review',
        closed:    'Closed',
        cancelled: 'Cancelled',
    },
    inventory_kind: {
        full:         'Full',
        partial:      'Partial',
        full_help:    'An item nobody counts is counted as 0 at close.',
        partial_help: 'Items nobody counts are left out of the result at close.',
    },
    inventory: {
        list_title:       'Stock counts',
        list_subtitle:    'The weekly bar count — opening, added, counted, sold and result.',
        new:              'New stock count',
        warehouse:        'Restaurant',
        all_warehouses:   'All restaurants',
        from:             'From date',
        to:               'To date',
        empty:            'No stock counts.',
        number:           'Number',
        date:             'Date',
        kind:             'Kind',
        status:           'Status',
        counted:          'Counted',
        counted_of:       '{counted} / {total}',
        difference_value: 'Difference in value',
        note:             'Note',
        period: 'Period',
        period_from: 'Period from',
        period_to: 'Period to',
    },
    inventory_create: {
        title:             'New stock count',
        description:       'The place\'s article list becomes the list to count; the opening is the last closed count.',
        warehouse_pick:    'Choose a restaurant',
        no_warehouses:     'No place with a bar you could open an inventory for.',
        file:              'Stock export',
        file_help:         'Excel (XLSX) or CSV with a header row, at most 10 MB.',
        file_hint:         'XLSX or CSV',
        previewing:        'Reading the file …',
        preview_title:     'First {shown} of {total} rows',
        mapping_title:     'Column mapping',
        mapping_help:      'Which column of the file is what. Code, name and quantity are required.',
        no_column:         '— none —',
        save_mapping:      'Remember the column mapping',
        save_mapping_help: 'The next import suggests it by itself.',
        open_one:          '{number} is open ({status}, {date}). Close or cancel it first.',
        open_it:           'Open the stock count',
        create:            'Create',
        created:           'Stock count {number} is open.',
        report_rows:       'Rows',
        report_items:      'Items',
        report_skipped:    'Skipped',
        open:              'Open the stock count',
        wrong_type:        'The file must be XLSX or CSV.',
        too_big:           'The file is larger than {mb} MB.',
        preview_failed:    'The file could not be read.',
        failed:            'The stock count could not be created.',
        from_previous: 'Starts where {number} ended ({date}).',
        period_order: 'The period cannot end before it starts.',
    },
    count: {
        start:                     'Count',
        progress:                  'Counted',
        search:                    'Name or code',
        clear_search:              'Clear the search',
        area:                      'Area',
        area_placeholder:          'e.g. bar, cold room',
        filter_all:                'All',
        filter_uncounted:          'Not counted',
        filter_counted:            'Counted',
        filter_recount:            'Recount',
        no_match:                  'No matches.',
        empty_filter:              'No items on this list.',
        more:                      '{n} more — narrow the search.',
        not_counted:               'Not counted',
        recount_badge:             'Recount',
        added_badge:               'added while counting',
        recount_alert:             '{n} item needs to be counted again. | {n} items need to be counted again.',
        entries:                   'Entries',
        entries_count:             '{n} entry | {n} entries',
        mine:                      'mine {quantity}',
        me:                        'me',
        counted_total:             'Counted in total',
        quantity:                  'Quantity',
        quantity_required:         'Enter the quantity.',
        note:                      'Note',
        code:                      'Code',
        unit:                      'Unit',
        unit_placeholder:          'kg, pcs, l …',
        name:                      'Name',
        add:                       'Add',
        added:                     'Added: {amount} — {name}',
        add_failed:                'The entry could not be added.',
        remove_entry:              'Remove the entry',
        remove_question:           'Remove the entry {quantity} {unit} for {name}?',
        entry_removed:             'The entry is removed.',
        read_only:                 'No entries can be added now.',
        no_permission:             'You may not count.',
        not_counting:              'The stock count is no longer being counted ({status}); no entries can be added.',
        open_summary:              'Open the summary',
        new_item:                  'Add an item that is not on the list',
        new_item_title:            'An item not on the list',
        new_item_help:             'Found behind the bar, missing from the place\'s list.',
        code_on_list:              '{code} is already on the list — opening it.',
        submit:                    'Finish counting',
        submit_confirm:            'Hand over to review',
        submit_question:           'Every item is counted. Hand the stock count over to review? Counting cannot continue after that.',
        submit_question_uncounted: '{n} of {total} items are still not counted. Hand the stock count over to review anyway?',
        submitted:                 'The stock count is handed over to review.',
        submit_failed:             'The stock count could not be handed over to review.',
    },
    review: {
        created_by:                 'opened by {name}, {when}',
        to_counting_screen:         'Counting',
        download:                   'Download Excel',
        download_all:               'All items',
        download_differences:       'Differences only',
        cancel:                     'Cancel the stock count',
        cancel_title:               'Cancel stock count {number}',
        cancel_help:                'A cancelled stock count stays on record but does not count. The restaurant can then get a new one.',
        back_to_counting:           'Back to counting',
        back_to_counting_question:  'Send the stock count back to counting? The counters will see the items marked for recount ({n}).',
        close:                      'Close',
        close_question_full:        'Close the stock count? Full count: the items not counted ({n}) are counted as 0. A closed stock count is locked.',
        close_question_partial:     'Close the stock count? Partial count: the items not counted ({n}) are left out of the result. A closed stock count is locked.',
        reopen:                     'Reopen',
        reopen_question:            'Send the closed stock count back to review?',
        done_back_to_counting:      'The stock count is being counted again.',
        done_close:                 'The stock count is closed.',
        done_reopen:                'The stock count is in review again.',
        done_cancel:                'The stock count is cancelled.',
        step_failed:                'The step could not be taken.',
        cancelled_title:            'Cancelled by {name}, {when}',
        closed_title:               'Closed by {name}, {when}',
        counting_title:             'Counting is under way',
        counting_help:              'The counters\' entries appear here as they come in. When they finish, the stock count goes to review.',
        submitted_title:            'Handed over to review by {name}, {when}',
        stat_counted:               'Counted',
        stat_uncounted:             '{n} not counted',
        stat_differences:           'Differences',
        stat_recount:               'recount: {n}',
        stat_difference_value:      'Difference in value',
        stat_book_value:            'Book value',
        stat_counted_value:         'counted {value}',
        stat_counters:              'Counters',
        items_title:                'Items',
        shown:                      '{n} of {total}',
        filter_all:                 'All',
        filter_differences:         'Differences only',
        filter_uncounted:           'Not counted',
        filter_recount:             'Recount',
        code:                       'Code',
        name:                       'Name',
        unit:                       'Unit',
        book:                       'Book',
        counted:                    'Counted',
        difference:                 'Difference',
        price:                      'Price',
        difference_value:           'Diff. value',
        entries:                    'Entries',
        recount:                    'Recount',
        recount_of:                 'Recount, {code}',
        expand:                     'Show the entries',
        uncounted_full:             '0 at close',
        uncounted_partial:          'left out',
        no_entries:                 'No entries.',
        manager_note:               'Note',
        manager_note_placeholder:   'Why it differs, what was found …',
        remove_entry_question:      'Remove the entry {quantity} {unit} ({name})?',
        correction_title:           'Count correction',
        correction_of:              'Count correction, {code}',
        correction_help:            'Adds an entry to what was counted. A count that is too high is put right by removing the wrong entry in the item\'s row.',
        correction_added:           'The correction is added.',
        article: 'Article',
        opening: 'Opening',
        added: 'Added',
        sold: 'Sold',
        result: 'Result',
        formula: 'Result = (Counted + Sold) − (Opening + Added)',
        shortage: 'Shortage',
        surplus: 'Surplus',
        filter_no_sales: 'No sales',
        stat_shortage: 'Shortage',
        stat_surplus: 'Surplus',
        stat_items_sum: 'total {sum}',
        stat_sales: 'With sales',
        stat_no_sales: 'import the sales',
        sold_of: 'Sold, {name}',
        added_of: 'Added, {name}',
        has_correction: 'corrected',
        added_help: 'Transfers received into the place minus those issued out of it in the period, plus the correction.',
        added_transfers: 'Transfers',
        no_transfers: 'No transfers of this article in the period.',
        transfer_from: 'from {place}',
        transfer_to: 'to {place}',
        added_transfers_total: 'Transfers in total',
        correction: 'Correction',
        correction_help_added: 'Added to the transfers (a negative one subtracts).',
        correction_note_placeholder: 'Why the correction …',
        correction_note_required: 'A correction needs a note.',
        correction_saved: 'The correction is saved.',
        transfers_title: 'Transfers in the period',
        transfers_subtitle: 'The transfer notes counted in Added.',
        transfer_in: 'in',
        transfer_out: 'out',
        transfers_window: 'Received and issued between {from} and {to}; counted in Added.',
        unmatched_alert: '{n} POS name is not linked to an article yet. | {n} POS names are not linked to an article yet.',
        unmatched_action: 'Link the names',
        opening_of: 'Opening, {name}',
    },
    article: {
        title:               'Bar articles',
        subtitle:            'The articles that move through the bars — each place\'s list in the order of its count sheet.',
        tab_place:           'By place',
        tab_all:             'All articles',
        place:               'Place',
        place_pick:          'Pick a place',
        no_bar_places:       'No place has a bar (Catalogs → Warehouses).',
        search:              'Search articles',
        add_existing:        'Add an existing article',
        add:                 'New article',
        edit:                'Edit article',
        on_list:             '{n} article | {n} articles',
        place_empty:         'The place has no list yet. Add articles or import a count sheet.',
        add_in_group:        'New article in {group}',
        move_up:             'Move up: {name}',
        move_down:           'Move down: {name}',
        move_to:             'Move to position: {name}',
        move_to_title:       'Move to position',
        position:            'Position',
        move:                'Move',
        remove_from_place:   'Remove from the list: {name}',
        remove_question:     'Remove {name} from this place\'s list? The article stays in the catalog.',
        remove:              'Remove from list',
        added_to_place:      '{name} is on the list.',
        removed_from_place:  '{name} is off the list.',
        name:                'Name',
        name_help:           'As the POS writes it; case and diacritics do not matter.',
        unit:                'Unit',
        group:               'Group',
        code:                'Code',
        code_help:           'Optional.',
        add_to_place:        'Add to the list of {place}',
        active_help:         'An inactive article is not offered on transfer notes.',
        name_taken:          'The article {name} already exists.',
        merge:               'Merge',
        merge_n:             'Merge ({n})',
        merge_hint:          'To merge, tick two or more spellings of one article.',
        merge_title:         'Merge articles',
        merge_help:          'Places, POS names, transfer and inventory lines move onto the article you keep. This cannot be undone.',
        merge_keep:          'Keep',
        merge_summary:       '{n} other record is merged into {keep}. | {n} other records are merged into {keep}.',
        merged:              'The articles are merged into {name}.',
        merge_failed:        'The articles could not be merged.',
        places_count:        '{n} place | {n} places',
        sales_names_count:   '{n} POS name | {n} POS names',
        select:              'Select {name}',
        empty:               'No articles.',
        places:              'Places · position',
        sales_names:         'POS',
        only_inactive:       'Inactive',
        deactivate_question: 'Remove the article {name}? If it is in use, it is only deactivated.',
        aliases: 'also: {names}',
        merge_max: 'At most {n} records can be merged at once.',
        merge_open_inventory: 'The articles cannot be merged while two of them are on one open inventory — close it first.',
        dialog_help: 'Name, unit, group and an optional code.',
        unit_help: 'Empty means "kos" (piece).',
    },
    sheet_import: {
        button:          'Import count sheet',
        title:           'Import a count sheet',
        description:     'The article column of a count sheet becomes the place\'s list in the same order.',
        place:           'Place',
        place_pick:      'Pick a place',
        file:            'Count sheet',
        file_help:       'Excel (XLSX) like "Popisni list La Pizzeria …", at most 10 MB.',
        file_hint:       'XLSX',
        opening:         'Use the count as the opening stock',
        opening_help:    'The last "POPIS ŠANK" column becomes the opening stock of the place\'s first inventory.',
        replaces_list:   'The place\'s list is replaced by the sheet\'s articles and order. Known articles are linked, new ones created.',
        done:            'The list of {place} is imported.',
        rows:            'Rows',
        linked:          'Linked',
        created:         'Created',
        skipped:         'Skipped',
        opening_set:     'The opening stock is set.',
        opening_set_n:   'The opening stock is set for {n} article. | The opening stock is set for {n} articles.',
        opening_not_set: 'The opening stock was not set.',
        wrong_type:      'The count sheet must be an XLSX.',
        sheet: 'Sheet in the workbook',
        sheet_help: 'Optional — empty means the sheet with the most data.',
        sheet_placeholder: 'e.g. Popis',
        sheet_read: 'Sheet read: {sheet}',
    },
    transfer_status: {
        draft:     'Draft',
        issued:    'Issued',
        received:  'Received',
        cancelled: 'Cancelled',
    },
    transfer_status_tab: {
        draft:     'Drafts',
        issued:    'Issued',
        received:  'Received',
        cancelled: 'Cancelled',
    },
    transfer: {
        list_title:            'Transfer notes',
        list_subtitle:         'Goods moved between places — issued by the sender, received by the target.',
        new:                   'New transfer note',
        new_title:             'New transfer note',
        edit_title:            'Edit transfer note',
        draft_number:          'draft',
        place:                 'Place',
        all_places:            'All places',
        any_article:           'Any article',
        article:               'Article',
        empty:                 'No transfer notes.',
        limit_hint:            'The latest {n} are shown; narrow the filter.',
        number:                'Number',
        date:                  'Date',
        route:                 'From → to',
        lines:                 'Lines',
        status:                'Status',
        people:                'Who',
        created_short:         'made by {name}',
        issued_short:          'issued by {name}',
        received_short:        'received by {name}',
        cancelled_short:       'cancelled by {name}',
        no_places:             'No places for transfers (Catalogs → Warehouses → Transfers), or you cannot read them.',
        from:                  'From',
        to:                    'To',
        place_pick:            'Pick',
        swap:                  'Swap from and to',
        note:                  'Note',
        lines_title:           'Lines ({n})',
        article_search:        'Find an article …',
        search_hint:           'Type a few letters of the article\'s name.',
        search_hint_bar:       'Type a few letters; the articles of {place} come first.',
        results_target:        '{place} list',
        results_other:         'Other articles',
        results_all:           'Articles',
        on_note:               'on the note {quantity}',
        quantity:              'Quantity',
        quantity_of:           'Quantity, {name}',
        line_note:             'Line note',
        no_lines:              'The note has no lines yet.',
        no_lines_error:        'Add at least one line.',
        remove_line:           'Remove the line {name}',
        line_summed:           '{name}: {quantity} in total',
        quantity_required:     'Enter a quantity.',
        quantity_positive:     'The quantity must be above 0.',
        quantity_not_negative: 'The quantity cannot be negative.',
        same_place:            'From and to must be different places.',
        save_draft:            'Save draft',
        saved:                 'The draft is saved.',
        save_failed:           'The transfer note could not be saved.',
        saved_as_draft:        'It stays saved as a draft.',
        issue:                 'Issue',
        issue_question:        'Issue the transfer note {from} → {to} ({n} line)? It gets its number and can no longer be edited. | Issue the transfer note {from} → {to} ({n} lines)? It gets its number and can no longer be edited.',
        issued:                'Transfer note {number} is issued.',
        issued_print_help:     'Print it and send it with the goods; the target confirms the receipt in the portal.',
        issue_failed:          'The transfer note could not be issued.',
        leave_question:        'The transfer note is not saved. Leave and discard the changes?',
        leave:                 'Discard',
        not_draft:             'Only a draft can be edited.',
        print:                 'Print',
        cancel:                'Cancel the note',
        cancel_title:          'Cancel transfer note {number}',
        cancel_help:           'A cancelled note stays on record but does not count in the inventory.',
        cancelled_done:        'The transfer note is cancelled.',
        cancel_failed:         'The transfer note could not be cancelled.',
        cancelled_title:       'Cancelled by {name}, {when}',
        waiting_receipt:       'Waiting for {place} to confirm the receipt.',
        receive:               'Confirm receipt',
        receive_title:         'Confirm receipt',
        receive_help:          'Enter how much actually arrived at {place}; the sent quantity is the default.',
        receive_confirm:       'Confirm receipt',
        receive_question:      'Everything arrived as sent. Confirm the receipt?',
        receive_question_diff: '{n} line differs from what was sent. Confirm the receipt? | {n} lines differ from what was sent. Confirm the receipt?',
        received_done:         'The receipt is confirmed.',
        receive_failed:        'The receipt could not be confirmed.',
        sent_quantity:         'sent {quantity} {unit}',
        received_of:           'Received, {name}',
        received_quantity:     'received {quantity}',
        receipt_note:          'Receipt note',
        receipt_note_help:     'What differs, and why.',
        history:               'History',
        history_created:       'Made',
        history_issued:        'Issued as {number}',
        history_received:      'Received at {place}',
        history_cancelled:     'Cancelled',
        lines_count: '{n} line | {n} lines',
        not_own: 'Only the person who made the draft can edit and issue it.',
        first_in_shift: 'First in shift at {place}: {name} from {time}',
    },
    print: {
        title:        'TRANSFER NOTE',
        draft_mark:   'DRAFT',
        company:      'Galerija Gosposka d.o.o.',
        company_line: 'Transfer of goods between warehouses',
        from:         'From (issuing place)',
        to:           'To (receiving place)',
        date:         'Date',
        no:           'No.',
        article:      'Article',
        unit:         'Unit',
        quantity:     'Quantity',
        received:     'Received',
        note:         'Note',
        line_count:   'Lines: {n}',
        issued_by:    'Issued by:',
        received_by:  'Received by:',
        at:           '{when}',
        date_sign:    'Date and signature',
        footer:       'Transfer note {number}',
        printed:      'Printed {when}',
    },
    sales_field: {
        name:     'Name',
        quantity: 'Quantity',
    },
    sales: {
        button:          'Import sales',
        title:           'Import sales',
        description:     'The POS sales export of the period ({place}); names are matched to the place\'s articles.',
        file:            'Sales export',
        file_help:       'Excel (XLSX) or CSV, at most 10 MB.',
        preview_title:   'The first {shown} rows (header on row {row})',
        mapping_help:    'Which column is the article name and which the quantity sold.',
        column_pick:     'Pick a column',
        replaces:        'Importing again replaces the quantities sold so far.',
        import:          'Import',
        failed:          'The sales could not be imported.',
        matched:         'Matched names',
        unmatched:       'Unmatched',
        unmatched_title: 'POS names without an article',
        unmatched_help:  'Link a name to one of the place\'s articles (remembered next time, its quantity added) or ignore it.',
        quantity:        'sold {quantity}',
        article_pick:    'Pick an article',
        link:            'Link',
        ignore:          'Ignore',
        ignored:         'ignored',
        link_failed:     'The name could not be linked.',
        later:           'Finish later',
        sets_all: 'The import sets Sold for every article — 0 for those the export does not name, manual values included.',
    },
    schedule: {
        week_title:            'Week {n}',
        previous:              'Previous week',
        next:                  'Next week',
        this_week:             'This week',
        today:                 'today',
        draft:                 'Draft',
        published:             'Published',
        published_by:          'Published by {name}, {when}',
        publish:               'Publish',
        publish_question:      'Publish the schedule for week {n}? Every employee will see it.',
        published_done:        'The schedule is published.',
        publish_failed:        'The schedule could not be published.',
        copy_previous:         'Copy the previous week',
        copy_question:         'Copy the shifts of week {n} onto the empty days of this week? Shifts already entered stay.',
        copy:                  'Copy',
        copied:                'The previous week\'s shifts were copied onto the empty days.',
        copy_failed:           'The previous week could not be copied.',
        print:                 'Print',
        excel:                 'Excel',
        not_published:         'The schedule for this week is not published yet.',
        draft_help:            'Draft — employees cannot see it yet. Publish it when it is ready.',
        employee:              'Employee',
        hours_short:           'Hours',
        day_hours:             '{hours} h',
        no_employees:          'No employees. Add them under Schedules → Employees.',
        nobody:                'Nobody is scheduled this day.',
        add_shift:             'Add a shift',
        edit_day:              'Edit the day',
        edit_shift_of:         'Edit the shift: {name}, {day}',
        inventory_place:       'Bar count: {place}',
        day_note:              'Note of the day',
        day_note_help:         'E.g. POPIS — shown in the day\'s header.',
        inventory_places:      'Bar count this day',
        inventory_places_help: 'The weekly count (Inventura) of these places; everybody in shift there gets a reminder on the overview.',
        inventory_places_none: 'No count',
        summary:               'Hours this week',
        summary_subtitle:      'Total {hours} h',
        day_total:             'Total',
        week_total:            'Week',
        copied_count: 'Shifts copied: {copied}; already entered and kept: {kept}.',
        copy_skipped: '{n} shift no longer passes the checks and was not copied. | {n} shifts no longer pass the checks and were not copied.',
        on_call:               'On call (if needed)',
        on_call_plus:          '+{hours} h on call',
        on_call_reserved:      'On call — not called in',
        on_call_present:       'Called in – was present',
        mark_present:          'Mark as present',
        unmark_present:        'Unmark',
        on_call_of:            'On call {time}: {name}',
        called_in_saved:       '{name} is marked as present.',
        called_in_cleared:     'Presence of {name} is unmarked.',
        called_in_failed:      'The presence could not be changed.',
    },
    shift: {
        mode_shift:      'Shift',
        mode_status:     'Whole day',
        segment:         'Part {n}',
        no_segments:     'No shift. Add a part, or save it empty to clear the day.',
        start:           'Start',
        end:             'End',
        next_day:        '(+1)',
        position:        'Position',
        place:           'Place',
        pick:            'Choose',
        add_segment:     'Add a part',
        remove_segment:  'Remove the part',
        status:          'Status of the day',
        no_statuses:     'No statuses. Add them under Settings → Schedules.',
        note:            'Note',
        copy_to:         'Same on',
        copy_to_help:    'The same day is also written to the chosen days of this week (replacing what is there).',
        hours:           '{hours} h',
        total:           'Total',
        clear:           'Clear the day',
        clear_question:  'Clear {day} for {name}?',
        last_edited:     'Last edited by {name}, {when}',
        problem_order:   'The end must come after the start.',
        problem_overlap: 'It overlaps another part.',
        problem_outside: 'The end is past the end of the day.',
        saved_days:      'Saved for {n} day. | Saved for {n} days.',
        on_call:         'On call (if needed)',
        on_call_help:    'The person comes only when called in; the hours count only once they are.',
        called_in:       'Called in – was present',
        on_call_plus:    '+{hours} h on call',
        called_in_needs_on_call: 'Only a segment on call can be called in.',
    },
    schedule_print: {
        mark_on_call:   'PP',
        mark_called_in: 'PP✓',
        title:     'SCHEDULE',
        subtitle:  'Shift roster',
        inventory: 'Bar count: {places}',
        legend:    'Legend',
        on_call:   'On call (if needed)',
        called_in: 'Called in – was present',
    },
    my_schedule: {
        title:           'My shifts',
        subtitle:        'Published shifts from {from} to {to}.',
        whole_week:      'Whole week',
        today:           'Today',
        tomorrow:        'Tomorrow',
        empty:           'You have no shifts in the published schedules for these days.',
        not_linked:      'Your account is not linked to anybody on the schedule.',
        not_linked_help: 'A schedule editor links you under Schedules → Employees.',
        on_call:         'On call',
        on_call_help:    'You come only if you are called in.',
        called_in:       'Called in',
    },
    employee: {
        title:           'Employees',
        subtitle:        'The schedule\'s rows in the sheet\'s order; linking a portal account gives the person "My shifts".',
        search:          'Search employees',
        show_inactive:   'Show inactive',
        empty:           'No employees.',
        add:             'Add an employee',
        edit:            'Edit the employee',
        name:            'Name',
        account:         'Portal account',
        account_help:    'The account the person signs in with; an account can be linked to one person only.',
        account_none:    'No account',
        linked:          'linked',
        home:            'Home',
        home_help:       'Free text as in the sheet\'s column (e.g. Rožmarin, reception, internship).',
        active:          'Active',
        active_help:     'An inactive employee is no longer drawn on the schedule (except in weeks where they have shifts).',
        state:           'State',
        move_up:         'Move up',
        move_down:       'Move down',
        order_help:      'The order cannot be changed while searching.',
        remove:          'Remove',
        remove_question: 'Remove {name} from the schedule? Somebody who already has shifts is only deactivated.',
        removed:         '{name} was removed from the schedule.',
    },
    settings_urniki: {
        title:               'Schedules',
        subtitle:            'Positions, day statuses and the day\'s time grid. Place colours are edited in Catalogs → Warehouses.',
        positions:           'Positions',
        positions_help:      'The letter written where a shift starts, a name and a colour.',
        add_position:        'Add a position',
        positions_required:  'At least one position.',
        statuses:            'Day statuses',
        statuses_help:       'A whole day that is not a shift (leave, sick leave …), with the colour of its band.',
        add_status:          'Add a status',
        no_statuses:         'No statuses.',
        code:                'Letter',
        code_is:             'Code: {code}',
        name:                'Name',
        color:               'Colour',
        color_invalid:       'A colour is written #RRGGBB.',
        codes_duplicate:     'Each letter can be used once.',
        names_duplicate:     'Each status can be used once.',
        status_code_invalid: 'The name needs at least one letter or digit.',
        day:                 'The schedule\'s day',
        day_help:            'The grid\'s start and end; shifts are entered in {minutes}-minute steps.',
        day_start:           'Day starts',
        day_end:             'Day ends',
        day_end_help:        'Before the start means the next morning (e.g. 03:00).',
        day_empty:           'The end must differ from the start.',
        code_of:           'Letter, row {n}',
        position_name_of:  'Position name, row {n}',
        position_color_of: 'Position colour, row {n}',
        status_name_of:    'Status name, row {n}',
        status_color_of:   'Status colour, row {n}',
        on_call:             'On call',
        on_call_help:        'The colour of time on call (if needed): lighter and dashed on the schedule until the person is called in.',
        on_call_color:       'On-call colour',
    },
    color: {
        none:        'no colour',
        placeholder: '#RRGGBB',
        hex:         'Colour (hex)',
        clear:       'No colour',
        invalid: 'Write the colour as #RRGGBB.',
    },
    user: {
        title:                   'Users',
        subtitle:                'Local accounts, their roles and warehouses.',
        search:                  'Search users',
        show_inactive:           'Show inactive',
        empty:                   'No users.',
        add:                     'Add user',
        edit:                    'Edit user',
        username:                'Username',
        username_help:           'Lower-case letters, digits, dot, underscore or hyphen (2–40). It never changes.',
        username_fixed:          'The username cannot be changed.',
        username_invalid:        'Only lower-case letters, digits, . _ - (2–40 characters).',
        username_taken:          'This username is already taken.',
        name:                    'Full name',
        email:                   'E-mail',
        password:                'Starting password',
        password_help:           'At least {n} characters. The person changes it at their first sign-in.',
        new_password:            'New password',
        roles:                   'Roles',
        roles_pick:              'Choose roles',
        warehouses:              'Units',
        warehouses_all:          'All units',
        warehouses_help:         'Empty means everything; otherwise only the delivery notes, stock counts and transfers of the chosen units and their places (Rožmarin: also Vinoteka, Glavno skladišče …), and delivery notes without a unit.',
        warehouses_unavailable:  'There are no warehouses yet.',
        active:                  'Active',
        inactive:                'Inactive',
        active_help:             'An inactive user cannot sign in; an open session ends at once.',
        state:                   'Status',
        must_change:             'Must change password',
        last_login:              'Last sign-in',
        never:                   'never',
        you:                     'you',
        no_access:               'No access',
        reset_password:          'Reset password',
        reset_password_title:    'New password for {name}',
        reset_password_help:     'The person will have to change it at their next sign-in.',
        password_reset_done:     'The password for {name} is reset.',
        password_reset_failed:   'The password could not be reset.',
        deactivate:              'Deactivate',
        deactivate_question:     'Deactivate {name}? They are signed out at once and cannot sign in.',
        deactivated:             '{name} is deactivated.',
        cannot_deactivate_self:  'You cannot deactivate yourself.',
        reactivate:              'Reactivate',
        reactivated:             '{name} is active again.',
    },
    role: {
        title:            'Roles',
        subtitle:         'A role is a named set of permissions; a user\'s permissions are the union of their roles.',
        empty:            'No roles.',
        add:              'Add role',
        edit:             'Edit role',
        view:             'Show role',
        name:             'Name',
        description:      'Description',
        permissions:      'Permissions',
        permissions_help: 'Every permission of a module includes viewing that module.',
        user_count:       'Users',
        system:           'system',
        locked_help:      'Skrbnik always holds every permission; the role cannot be changed.',
        all:              'All',
        none:             'None',
        name_taken:       'A role with this name already exists.',
        delete_question:  'Delete role {name}?',
        in_use:           'The role cannot be deleted while a user has it.',
        system_no_delete: 'A system role cannot be deleted.',
        used_no_delete:   '{n} user has it. | {n} users have it.',
    },
    settings: {
        tab_ai:        'AI',
        tab_dobavnice: 'Delivery-note export',
        tab_share:     'Scanner (folder)',
        tab_inventura: 'Stock count',
        tab_urniki: 'Schedules',
        tab_mail:    'Mail',
        tab_eracuni: 'E-invoices',
    },
    settings_ai: {
        title:               'Reading with AI',
        subtitle:            'Claude (Anthropic) reads the scans; the server does the checks and matching.',
        enabled:             'Reading on',
        enabled_help:        'Off: new uploads wait in the queue.',
        key:                 'Anthropic API key',
        key_help:            'The key is stored encrypted and never shown again.',
        key_kept_help:       'The stored key stays until you replace or remove it.',
        key_remove_help:     'The key is removed on save; reading stops.',
        key_stored:          'Key stored (…{hint})',
        key_replace:         'Replace',
        key_remove:          'Remove',
        key_will_remove:     'The key will be removed',
        key_show:            'Show key',
        key_hide:            'Hide key',
        model:               'Model',
        max_concurrent:      'Parallel readings',
        max_concurrent_help: 'How many files are read at once (1–4).',
        test:                'Test the connection',
        test_ok:             'The connection works: {model}, {ms} ms.',
        test_failed:         'The connection does not work.',
    },
    settings_export: {
        title:               'Delivery-note export (XML)',
        subtitle:            'The file format for accounting; several values still await accounting\'s confirmation.',
        own_tax_id:          'Our tax ID',
        own_tax_id_help:     'The buyer on every document; never taken for the supplier.',
        vrsta_prometa:       'VrstaPrometa',
        vrsta_prometa_help:  'The value of the VrstaPrometa field in the export.',
        encoding:            'Encoding',
        decimal_separator:   'Decimal separator',
        separator_dot:       'dot (126.5)',
        separator_comma:     'comma (126,5)',
        amount_decimals:     'Amount decimals',
        quantity_decimals:   'Quantity decimals',
        sum_tolerance:       'Sum tolerance',
        sum_tolerance_help:  'The largest difference between the lines\' sum and the document\'s net amount.',
        vat_groups:          'VAT groups',
        vat_groups_help:     'VAT rate → GrupaDdv in the export.',
        vat_add:             'Add a rate',
        vat_rate_of:         'VAT rate, row {n}',
        vat_code_of:         'VAT group, row {n}',
        vat_duplicate:       'Each rate may appear only once.',
    },
    settings_share: {
        title:                'Scanner (network folder)',
        subtitle:             'The server watches the folders the scanner saves into — each belongs to a unit — and uploads each new file as a manual upload would.',
        enabled:              'Automatic import on',
        enabled_help:         'Switching it on needs at least one enabled folder, a user and a password.',
        path:                 'Shared folder',
        folders:              'Scanner folders',
        folders_help:         'For example {example}. A delivery note from a folder gets its unit, whatever the address on the document; without a unit the server guesses it from the address.',
        folders_empty:        'No folder yet.',
        folder_add:           'Add a folder',
        folder_pick:          'Add from the server',
        folder_unit:          'Unit',
        folder_no_unit:       'No unit (by address)',
        folder_enabled:       'On',
        folder_remove:        'Remove the folder',
        folder_twice:         'A folder is listed twice.',
        folder_test_ok:       'Works: {n} scan waits. | Works: {n} scans wait.',
        path_invalid:         'A path like \\\\server\\share\\folder.',
        username:             'User',
        username_help:        'Forms: {forms}.',
        form_user:            'user',
        form_domain:          'domain',
        domain:               'Domain',
        domain_help:          'Only for a user without a domain; empty for an account of the server itself.',
        password:             'Password',
        password_help:        'The password is stored encrypted and never shown again.',
        password_kept_help:   'The stored password stays until you replace or remove it.',
        password_remove_help: 'The password is removed on saving; the import stops.',
        password_stored:      'Password stored',
        password_replace:     'Replace',
        password_remove:      'Remove',
        password_will_remove: 'The password will be removed',
        password_show:        'Show password',
        password_hide:        'Hide password',
        processed_folder:     'Folder for processed files',
        processed_help:       'The subfolder every file taken is moved into (also one uploaded before).',
        failed_folder:        'Folder for refused files',
        failed_help:          'The subfolder for files the portal refuses (not a PDF, JPG or PNG, over 25 MB, empty).',
        folder_invalid:       'A folder name may not contain {chars}.',
        folders_differ:       'The two folders must differ.',
        poll_minutes:         'Look every (min)',
        poll_help:            'From 1 to 60 minutes.',
        min_age:              'Minimum file age (s)',
        min_age_help:         'Younger files wait for the next look — the scanner may still be writing them.',
        test:                 'Test the connection',
        test_help:            'The test uses the saved settings: in every folder it connects, counts the scans and makes the subfolders when missing; it also offers the folders beside the first one. It moves nothing.',
        test_ok:              'The connection works: {n} scan waits in the folders. | The connection works: {n} scans wait in the folders.',
        test_failed:          'The connection does not work.',
        test_save_first:      'Save the changes first — the test uses the saved settings.',
        test_folders_failed:  'The connection works, but the subfolders cannot be made.',
    },
    settings_mail: {
        title:                'Mail (outgoing)',
        subtitle:             'The SMTP account the portal sends e-mail with — first the e-invoices, notifications later.',
        host:                 'SMTP server',
        host_help:            'The server\'s name or address, without a protocol.',
        host_invalid:         'Only the server\'s name or address (no spaces, slashes or {\'@\'}).',
        security:             'Security',
        security_starttls:    'STARTTLS (587)',
        security_tls:         'TLS (465)',
        security_none:        'No encryption',
        port:                 'Port',
        username:             'User',
        username_help:        'Empty: the server needs no sign-in.',
        password:             'Password',
        password_help:        'The password is stored encrypted and never shown again.',
        password_kept_help:   'The stored password stays until you replace or remove it.',
        password_remove_help: 'The password will be removed on save.',
        password_stored:      'Password stored',
        password_replace:     'Replace',
        password_remove:      'Remove',
        password_will_remove: 'The password will be removed',
        password_show:        'Show password',
        password_hide:        'Hide password',
        from_address:         'Sender address',
        from_address_help:    'The From address; the server must allow sending with it.',
        from_name:            'Sender name',
        plain_warning:        'Without encryption the portal sends only to a server on a private network, and a password only to localhost.',
        test_title:           'Test message',
        test_help:            'Sends a short message with the saved settings to the address you type.',
        test_to:              'Test recipient',
        test:                 'Send a test message',
        test_ok:              'The message was sent to {to}.',
        test_failed:          'The message could not be sent.',
        test_save_first:      'Save your changes first — the test uses the saved settings.',
    },
    settings_eracuni: {
        title:                     'E-invoices',
        subtitle:                  'The server watches the folder of received e-invoices and e-mails every new file to the e-invoice intake.',
        enabled:                   'Sending on',
        enabled_help:              'Switching on needs a path, an account for the folder and the outgoing mail (Settings → Mail).',
        path:                      'Shared folder',
        path_help:                 'For example {example}. The server takes only the files in the folder itself, not in its subfolders.',
        path_required:             'A path is required to switch on.',
        use_scanner_account:       'Use the scanner\'s account',
        use_scanner_account_help:  'The user and password of the Scanner (folder) tab; off: an account of its own for this folder.',
        to:                        'Recipient',
        to_help:                   'The e-invoice intake address; every file goes in a message of its own.',
        subject:                   'Subject',
        body:                      'Text',
        template_help:             '{\'{file}\'} is replaced by the file\'s name.',
        sent_folder:               'Folder for sent files',
        sent_folder_help:          'The subfolder a sent file is moved into (also one sent before).',
        failed_folder:             'Folder for failed files',
        failed_folder_help:        'The subfolder for files over 25 MB, empty, or not sent after every attempt.',
        min_age_help:              'Younger files wait for the next look — they may still be being written.',
        extensions:                'Extensions',
        extensions_help:           'Only files with these extensions (no dot), e.g. pdf, xml.',
        extensions_add:            'Add an extension …',
        extensions_invalid:        'An extension is letters and digits only (1–10), e.g. pdf.',
        max_attempts:              'Most attempts',
        max_attempts_help:         'After this many failed sends the file goes to the failed folder.',
        test_help:                 'The test uses the saved settings: it connects, counts the files and makes the missing subfolders. It moves and sends nothing.',
        test_ok:                   'The connection works: {n} file is waiting. | The connection works: {n} files are waiting.',
    },
    eracuni_status: {
        sent:      'Sent',
        duplicate: 'Already sent',
        failed:    'Failed',
        gave_up:   'Given up',
        refused:   'Refused',
    },
    eracuni: {
        title:            'E-invoice log',
        subtitle:         'Every file of the e-invoice folder: what was sent, when and where.',
        settings:         'Settings',
        status_line:      'Folder {path}: last look {last}, today {sent}',
        status_sent:      '{n} sent | {n} sent',
        status_failed:    '{n} failed',
        status_waiting:   '{n} waiting in the folder',
        status_never:     'not yet',
        status_off:       'Sending from {path} is switched off.',
        status_error:     'Last error: {error}',
        search:           'File name, SHA-256 or Message-ID',
        status:           'Status',
        all_statuses:     'All statuses',
        from:             'Read from',
        to_date:          'Read until',
        empty:            'The log is empty.',
        empty_filtered:   'No file matches the filter.',
        read_at:          'Read',
        read_at_long:     'Read {at}',
        file:             'File',
        detail:           'Details',
        attempts:         'Attempts',
        resend_badge:     'sent again',
        duplicate_detail: 'The same content was sent before; moved to {folder}.',
        sent_detail:      'To {to}, {at}',
        reason:           'Reason',
        error:            'Server\'s answer',
        size:             'Size',
        to:               'Recipient',
        sent_at:          'Sent',
        last_attempt:     'last {at}',
        message_id:       'Message-ID',
        moved_to:         'Moved to',
        not_moved:        'still in the folder',
        duplicate_of:     'Sent before',
        resend_of:        'Sent again',
        resent_by:        'by {name}',
        open_earlier:     'Every entry of this content',
        sha256:           'SHA-256',
        no_copy:          'No copy is kept — the file was never read.',
        resend:           'Send again',
        resend_title:     'Send again',
        resend_question:  'The kept copy of {name} will be sent again to the recipient of the e-invoice settings.',
        resent_toast:     '{name} was sent again to {to}.',
        resend_failed:    'The file could not be sent again.',
    },
    settings_inventura: {
        title:                  'Stock count',
        subtitle:               'The POS sales import, and the counting areas.',
        mapping:                'Remembered sales column mapping',
        mapping_help:           'Set while importing sales ("Remember the column mapping"). Without it the server guesses from the column names.',
        not_set:                'not set',
        mapping_reset:          'Forget the mapping',
        mapping_reset_question: 'Forget the remembered column mapping? The next import will guess the columns from their names.',
        areas:                  'Areas',
        areas_help:             'Suggestions on the counting screen (e.g. bar, cold room). Counters may type another area too.',
        areas_duplicate:        'Each area may appear only once.',
    },
    profile: {
        title:             'My profile',
        subtitle:          'Name, e-mail and the console\'s language.',
        locale_help:       'The console\'s language after signing in.',
        must_change_title: 'Change your password',
        must_change_help:  'An administrator set your password. Set your own before you continue.',
    },
    password: {
        title:    'Password',
        subtitle: 'Type your current password to change it.',
        current:  'Current password',
        new:      'New password',
        repeat:   'Repeat the new password',
        mismatch: 'The passwords do not match.',
        same:     'The new password must differ from the current one.',
        change:   'Change password',
        changed:  'The password is changed.',
        failed:   'The password could not be changed.',
    },
} satisfies MessageSchema
